import React, { useState, useEffect } from 'react';
import {
  subscribe,
  getDashboardOverview,
  getSupplierPerformance,
  getOnboardingPipeline,
  getPerformanceMonitoring,
  getCommunicationHub,
  getRiskAssessments,
  getDevelopmentPrograms,
  getAlerts,
  getSupplierFeedback,
  getTopPerformers,
  getUnderperformers,
  getHighRiskSuppliers,
  getCriticalAlerts,
  getOnboardingBottlenecks,
  getPerformanceTrends,
  refreshData
} from '../../../services/ethics/supplierEcosystemService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';

const EthicsSupplierEcosystem = () => {
  const [ecosystemData, setEcosystemData] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [filterTier, setFilterTier] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => {
    const unsubscribe = subscribe(setEcosystemData);
    setLoading(false);
    
    // Log access
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'page_access', 
      target: 'supplier_ecosystem_ethics', 
      status: 'success' 
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefreshData = () => {
    setLoading(true);
    refreshData();
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'data_refresh', 
      target: 'supplier_ecosystem_data', 
      status: 'success' 
    });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (loading || !ecosystemData) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading supplier ecosystem data...</span>
          </div>
          <p className="mt-2">Loading Supplier Ecosystem Management...</p>
        </div>
      </div>
    );
  }

  const dashboardOverview = getDashboardOverview();
  const supplierPerformance = getSupplierPerformance();
  const onboardingPipeline = getOnboardingPipeline();
  const performanceMonitoring = getPerformanceMonitoring();
  const communicationHub = getCommunicationHub();
  const riskAssessments = getRiskAssessments();
  const developmentPrograms = getDevelopmentPrograms();
  const alerts = getAlerts();
  const supplierFeedback = getSupplierFeedback();
  const topPerformers = getTopPerformers();
  const underperformers = getUnderperformers();
  const highRiskSuppliers = getHighRiskSuppliers();
  const criticalAlerts = getCriticalAlerts();
  const onboardingBottlenecks = getOnboardingBottlenecks();
  const performanceTrends = getPerformanceTrends();

  // Filter suppliers based on criteria
  const filteredSuppliers = supplierPerformance.filter(supplier => {
    if (filterTier && supplier.tier !== filterTier) return false;
    if (filterCategory && supplier.category !== filterCategory) return false;
    return true;
  });

  const DashboardTab = () => (
    <div className="row">
      {/* KPI Cards */}
      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-primary text-white">
          <div className="card-body text-center">
            <div className="h2 mb-2">{dashboardOverview.activeSuppliers}</div>
            <div className="mb-2">Active Suppliers</div>
            <small>of {dashboardOverview.totalSuppliers} total</small>
          </div>
        </div>
      </div>

      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-success text-white">
          <div className="card-body text-center">
            <div className="h2 mb-2">{dashboardOverview.ethicsCertified}</div>
            <div className="mb-2">Ethics Certified</div>
            <small>{dashboardOverview.complianceRate}% compliance rate</small>
          </div>
        </div>
      </div>

      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-warning text-dark">
          <div className="card-body text-center">
            <div className="h2 mb-2">{dashboardOverview.underReview}</div>
            <div className="mb-2">Under Review</div>
            <small>Performance monitoring</small>
          </div>
        </div>
      </div>

      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100 bg-info text-white">
          <div className="card-body text-center">
            <div className="h2 mb-2">{dashboardOverview.averagePerformance}%</div>
            <div className="mb-2">Average Performance</div>
            <small>Across all KPIs</small>
          </div>
        </div>
      </div>

      {/* Critical Alerts */}
      {criticalAlerts.length > 0 && (
        <div className="col-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>Critical Alerts Requiring Immediate Attention
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                {criticalAlerts.map((alert, index) => (
                  <div key={index} className="col-md-4 mb-3">
                    <div className="card border-danger">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">{alert.type}</h6>
                          <span className="badge bg-danger">{alert.severity}</span>
                        </div>
                        <p className="small mb-2">{alert.message}</p>
                        <div className="d-flex justify-content-between">
                          <small><strong>Supplier:</strong> {alert.supplier}</small>
                          <small><strong>Due:</strong> {new Date(alert.dueDate).toLocaleDateString()}</small>
                        </div>
                        <div className="mt-2">
                          <small><strong>Action:</strong> {alert.action}</small>
                        </div>
                        <div className="mt-1">
                          <small><strong>Assigned:</strong> {alert.assigned}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Performance Trends */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>Performance Trends
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Overall Performance</span>
                <span className="text-success">{performanceTrends.overallPerformance.trend}</span>
              </div>
              <div className="progress mb-1" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-primary" 
                  style={{ width: `${performanceTrends.overallPerformance.current}%` }}
                ></div>
              </div>
              <small className="text-muted">{performanceTrends.overallPerformance.current.toFixed(1)}% Average Score</small>
            </div>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Ethics Compliance</span>
                <span className="text-success">{performanceTrends.ethicsCompliance.trend}</span>
              </div>
              <div className="progress mb-1" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-success" 
                  style={{ width: `${performanceTrends.ethicsCompliance.current}%` }}
                ></div>
              </div>
              <small className="text-muted">{performanceTrends.ethicsCompliance.current.toFixed(1)}% Average Score</small>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Sustainability</span>
                <span className="text-success">{performanceTrends.sustainability.trend}</span>
              </div>
              <div className="progress mb-1" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-info" 
                  style={{ width: `${performanceTrends.sustainability.current}%` }}
                ></div>
              </div>
              <small className="text-muted">{performanceTrends.sustainability.current.toFixed(1)}% Average Score</small>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performers */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-star me-2"></i>Top Performing Suppliers
            </h5>
          </div>
          <div className="card-body">
            {topPerformers.slice(0, 5).map((supplier, index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 className="mb-0">{supplier.name}</h6>
                    <small className="text-muted">{supplier.tier} • {supplier.category}</small>
                  </div>
                  <div className="text-center">
                    <div className="h5 mb-0 text-success">{supplier.overallScore}%</div>
                    <small className="text-muted">Overall Score</small>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3">
                    <div className="text-center">
                      <div className="small fw-bold">{supplier.kpiScores.quality}</div>
                      <div className="progress mt-1" style={{ height: '4px' }}>
                        <div 
                          className="progress-bar bg-primary" 
                          style={{ width: `${supplier.kpiScores.quality}%` }}
                        ></div>
                      </div>
                      <small className="text-muted">Quality</small>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="text-center">
                      <div className="small fw-bold">{supplier.kpiScores.ethics}</div>
                      <div className="progress mt-1" style={{ height: '4px' }}>
                        <div 
                          className="progress-bar bg-success" 
                          style={{ width: `${supplier.kpiScores.ethics}%` }}
                        ></div>
                      </div>
                      <small className="text-muted">Ethics</small>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="text-center">
                      <div className="small fw-bold">{supplier.kpiScores.sustainability}</div>
                      <div className="progress mt-1" style={{ height: '4px' }}>
                        <div 
                          className="progress-bar bg-info" 
                          style={{ width: `${supplier.kpiScores.sustainability}%` }}
                        ></div>
                      </div>
                      <small className="text-muted">Sustainability</small>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="text-center">
                      <div className="small fw-bold">{supplier.kpiScores.business}</div>
                      <div className="progress mt-1" style={{ height: '4px' }}>
                        <div 
                          className="progress-bar bg-warning" 
                          style={{ width: `${supplier.kpiScores.business}%` }}
                        ></div>
                      </div>
                      <small className="text-muted">Business</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Onboarding Pipeline */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-plus-circle me-2"></i>Onboarding Pipeline
            </h5>
          </div>
          <div className="card-body">
            {onboardingPipeline.slice(0, 5).map((supplier, index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 className="mb-0">{supplier.supplierName}</h6>
                    <small className="text-muted">{supplier.category}</small>
                  </div>
                  <span className={`badge ${
                    supplier.priority === 'High' ? 'bg-danger' : 
                    supplier.priority === 'Medium' ? 'bg-warning' : 'bg-success'
                  }`}>
                    {supplier.priority}
                  </span>
                </div>
                <div className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <small>Current Stage: {supplier.currentStage}</small>
                    <small>{supplier.progress}%</small>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div 
                      className="progress-bar bg-info" 
                      style={{ width: `${supplier.progress}%` }}
                    ></div>
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <small><strong>Value:</strong> ${(supplier.potentialValue / 1000000).toFixed(1)}M</small>
                  <small><strong>Est. Completion:</strong> {new Date(supplier.estimatedCompletion).toLocaleDateString()}</small>
                </div>
                <div className="mt-1">
                  <small><strong>Assigned:</strong> {supplier.assignedTo}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* High-Risk Suppliers */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>High-Risk Suppliers
            </h5>
          </div>
          <div className="card-body">
            {highRiskSuppliers.length > 0 ? (
              highRiskSuppliers.slice(0, 5).map((supplier, index) => (
                <div key={index} className="mb-3 p-3 border rounded">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <h6 className="mb-0">{supplier.name}</h6>
                      <small className="text-muted">{supplier.tier} • {supplier.category}</small>
                    </div>
                    <span className="badge bg-danger">{supplier.overallScore}%</span>
                  </div>
                  <div className="mb-2">
                    <small className="fw-bold text-danger">Key Issues:</small>
                    {supplier.keyIssues.slice(0, 2).map((issue, idx) => (
                      <div key={idx} className="small text-muted">• {issue}</div>
                    ))}
                  </div>
                  <div className="d-flex justify-content-between">
                    <small><strong>Status:</strong> {supplier.status}</small>
                    <small><strong>Next Review:</strong> {new Date(supplier.nextAssessment).toLocaleDateString()}</small>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-success">
                <i className="fas fa-check-circle fa-3x mb-3"></i>
                <p>No high-risk suppliers identified</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const SuppliersTab = () => (
    <div className="row">
      {/* Filter Controls */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-filter me-2"></i>Filter Suppliers
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <label className="form-label">Tier</label>
                <select 
                  className="form-select"
                  value={filterTier}
                  onChange={(e) => setFilterTier(e.target.value)}
                >
                  <option value="">All Tiers</option>
                  <option value="Tier 1">Tier 1</option>
                  <option value="Tier 2">Tier 2</option>
                  <option value="Tier 3">Tier 3</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Category</label>
                <select 
                  className="form-select"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Technology">Technology</option>
                  <option value="Raw Materials">Raw Materials</option>
                  <option value="Services">Services</option>
                  <option value="Logistics">Logistics</option>
                </select>
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <button 
                  className="btn btn-outline-secondary me-2"
                  onClick={() => { setFilterTier(''); setFilterCategory(''); }}
                >
                  <i className="fas fa-times me-2"></i>Clear Filters
                </button>
                <span className="badge bg-info">
                  {filteredSuppliers.length} suppliers found
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Suppliers Table */}
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-building me-2"></i>Supplier Performance Overview
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Supplier</th>
                    <th>Tier</th>
                    <th>Category</th>
                    <th>Location</th>
                    <th>Overall Score</th>
                    <th>Quality</th>
                    <th>Ethics</th>
                    <th>Sustainability</th>
                    <th>Business</th>
                    <th>Risk Level</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.slice(0, 20).map((supplier, index) => (
                    <tr key={index}>
                      <td>
                        <div className="fw-bold">{supplier.name}</div>
                        <small className="text-muted">{supplier.id}</small>
                      </td>
                      <td><span className="badge bg-secondary">{supplier.tier}</span></td>
                      <td>{supplier.category}</td>
                      <td>{supplier.location}</td>
                      <td>
                        <span className={`badge ${
                          supplier.overallScore >= 85 ? 'bg-success' : 
                          supplier.overallScore >= 75 ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {supplier.overallScore}%
                        </span>
                      </td>
                      <td>{supplier.kpiScores.quality}%</td>
                      <td>{supplier.kpiScores.ethics}%</td>
                      <td>{supplier.kpiScores.sustainability}%</td>
                      <td>{supplier.kpiScores.business}%</td>
                      <td>
                        <span className={`badge ${
                          supplier.riskLevel === 'Low' ? 'bg-success' : 
                          supplier.riskLevel === 'Medium' ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {supplier.riskLevel}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          supplier.status === 'Active' ? 'bg-success' : 
                          supplier.status === 'Monitoring' ? 'bg-warning' : 'bg-danger'
                        }`}>
                          {supplier.status}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => setSelectedSupplier(supplier)}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-secondary">
                          <i className="fas fa-download"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const OnboardingTab = () => (
    <div className="row">
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-plus-circle me-2"></i>Supplier Onboarding Pipeline
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Supplier Name</th>
                    <th>Category</th>
                    <th>Current Stage</th>
                    <th>Progress</th>
                    <th>Priority</th>
                    <th>Potential Value</th>
                    <th>Start Date</th>
                    <th>Est. Completion</th>
                    <th>Assigned To</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {onboardingPipeline.map((supplier, index) => (
                    <tr key={index}>
                      <td className="fw-bold">{supplier.supplierName}</td>
                      <td>{supplier.category}</td>
                      <td>{supplier.currentStage}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="progress me-2" style={{ width: '80px', height: '6px' }}>
                            <div 
                              className="progress-bar bg-info" 
                              style={{ width: `${supplier.progress}%` }}
                            ></div>
                          </div>
                          <span className="small">{supplier.progress}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          supplier.priority === 'High' ? 'bg-danger' : 
                          supplier.priority === 'Medium' ? 'bg-warning' : 'bg-success'
                        }`}>
                          {supplier.priority}
                        </span>
                      </td>
                      <td>${(supplier.potentialValue / 1000000).toFixed(1)}M</td>
                      <td>{new Date(supplier.startDate).toLocaleDateString()}</td>
                      <td>{new Date(supplier.estimatedCompletion).toLocaleDateString()}</td>
                      <td>{supplier.assignedTo}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-1">
                          <i className="fas fa-eye"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-success">
                          <i className="fas fa-play"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Bottlenecks */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-hourglass-half me-2"></i>Onboarding Bottlenecks
            </h5>
          </div>
          <div className="card-body">
            {onboardingBottlenecks.map((bottleneck, index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">{bottleneck.stage}</h6>
                  <span className="badge bg-warning text-dark">{bottleneck.count} suppliers</span>
                </div>
                <div className="progress mt-2" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar bg-warning" 
                    style={{ width: `${(bottleneck.count / onboardingPipeline.length) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Development Programs */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-graduation-cap me-2"></i>Development Programs
            </h5>
          </div>
          <div className="card-body">
            {developmentPrograms.map((program, index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">{program.name}</h6>
                  <span className={`badge ${
                    program.status === 'Active' ? 'bg-success' : 
                    program.status === 'Completed' ? 'bg-info' : 'bg-secondary'
                  }`}>
                    {program.status}
                  </span>
                </div>
                <p className="small text-muted mb-2">{program.description}</p>
                <div className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <small>Progress</small>
                    <small>{program.completion}%</small>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div 
                      className="progress-bar bg-success" 
                      style={{ width: `${program.completion}%` }}
                    ></div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <small><strong>Participants:</strong> {program.participants}</small>
                  </div>
                  <div className="col-6">
                    <small><strong>Duration:</strong> {program.duration}</small>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <small><strong>Budget:</strong> {program.budget}</small>
                  </div>
                  <div className="col-6">
                    <small><strong>ROI:</strong> {program.roi}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const CommunicationTab = () => (
    <div className="row">
      {/* Communication Hub */}
      <div className="col-md-8 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-comments me-2"></i>Communication Hub
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Type</th>
                    <th>Supplier</th>
                    <th>Date</th>
                    <th>Participants</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {communicationHub.map((communication, index) => (
                    <tr key={index}>
                      <td className="fw-bold">{communication.type}</td>
                      <td>{communication.supplier}</td>
                      <td>{new Date(communication.date).toLocaleDateString()}</td>
                      <td>
                        <div className="small">
                          {communication.participants.slice(0, 2).map((participant, idx) => (
                            <div key={idx}>{participant}</div>
                          ))}
                          {communication.participants.length > 2 && (
                            <div>+{communication.participants.length - 2} more</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          communication.priority === 'High' ? 'bg-danger' : 
                          communication.priority === 'Medium' ? 'bg-warning' : 'bg-success'
                        }`}>
                          {communication.priority}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          communication.status === 'Completed' ? 'bg-success' : 
                          communication.status === 'Scheduled' ? 'bg-info' : 'bg-warning'
                        }`}>
                          {communication.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-1">
                          <i className="fas fa-eye"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-secondary">
                          <i className="fas fa-calendar"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Supplier Feedback */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">
              <i className="fas fa-star me-2"></i>Supplier Feedback
            </h5>
          </div>
          <div className="card-body">
            {supplierFeedback.map((feedback, index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">{feedback.supplier}</h6>
                  <div className="d-flex align-items-center">
                    <span className="me-1">{feedback.rating}</span>
                    <i className="fas fa-star text-warning"></i>
                  </div>
                </div>
                <p className="small mb-2">{feedback.feedback}</p>
                <div className="small text-muted mb-2">
                  <strong>Category:</strong> {feedback.category}
                </div>
                <div className="small text-muted">
                  <strong>Date:</strong> {new Date(feedback.date).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Monitoring Alerts */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>Performance Monitoring Alerts
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {performanceMonitoring.map((alert, index) => (
                <div key={index} className="col-md-4 mb-3">
                  <div className="card border-warning">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">{alert.type}</h6>
                        <span className={`badge ${
                          alert.severity === 'High' ? 'bg-danger' : 
                          alert.severity === 'Medium' ? 'bg-warning' : 'bg-info'
                        }`}>
                          {alert.severity}
                        </span>
                      </div>
                      <div className="small mb-2">
                        <strong>Supplier:</strong> {alert.supplier}
                      </div>
                      <div className="small mb-2">
                        <strong>Metric:</strong> {alert.metric}
                      </div>
                      <div className="small mb-2">
                        <strong>Current:</strong> {alert.currentValue} (Threshold: {alert.threshold})
                      </div>
                      <div className="small mb-2">
                        <strong>Trend:</strong> {alert.trend} for {alert.duration}
                      </div>
                      <div className="small">
                        <strong>Impact:</strong> {alert.impact}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">Supplier Ecosystem Ethics</h2>
              <p className="text-muted">Comprehensive supplier ethics and compliance management</p>
            </div>
            <button 
              className="btn btn-outline-primary"
              onClick={handleRefreshData}
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-2"></i>Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-pills">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: 'fas fa-tachometer-alt' },
              { id: 'suppliers', label: 'Suppliers', icon: 'fas fa-building' },
              { id: 'onboarding', label: 'Onboarding', icon: 'fas fa-plus-circle' },
              { id: 'communication', label: 'Communication', icon: 'fas fa-comments' }
            ].map(tab => (
              <li key={tab.id} className="nav-item">
                <button
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <i className={`${tab.icon} me-2`}></i>{tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'dashboard' && <DashboardTab />}
        {activeTab === 'suppliers' && <SuppliersTab />}
        {activeTab === 'onboarding' && <OnboardingTab />}
        {activeTab === 'communication' && <CommunicationTab />}
      </div>

      {/* Selected Supplier Detail Modal */}
      {selectedSupplier && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-building me-2"></i>
                  {selectedSupplier.name} - Supplier Details
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSelectedSupplier(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Performance Overview</h6>
                    <div className="mb-2">
                      <strong>Overall Score:</strong> {selectedSupplier.overallScore}%
                    </div>
                    <div className="mb-2">
                      <strong>Quality:</strong> {selectedSupplier.kpiScores.quality}%
                    </div>
                    <div className="mb-2">
                      <strong>Ethics:</strong> {selectedSupplier.kpiScores.ethics}%
                    </div>
                    <div className="mb-2">
                      <strong>Sustainability:</strong> {selectedSupplier.kpiScores.sustainability}%
                    </div>
                    <div className="mb-2">
                      <strong>Business:</strong> {selectedSupplier.kpiScores.business}%
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6>Supplier Information</h6>
                    <div className="mb-2">
                      <strong>Tier:</strong> {selectedSupplier.tier}
                    </div>
                    <div className="mb-2">
                      <strong>Category:</strong> {selectedSupplier.category}
                    </div>
                    <div className="mb-2">
                      <strong>Location:</strong> {selectedSupplier.location}
                    </div>
                    <div className="mb-2">
                      <strong>Relationship:</strong> {selectedSupplier.relationship}
                    </div>
                    <div className="mb-2">
                      <strong>Contract Value:</strong> ${(selectedSupplier.contractValue / 1000000).toFixed(1)}M
                    </div>
                  </div>
                </div>
                <hr />
                <div className="row">
                  <div className="col-md-6">
                    <h6>Strengths</h6>
                    {selectedSupplier.strongPoints.map((strength, idx) => (
                      <div key={idx} className="small text-success">• {strength}</div>
                    ))}
                  </div>
                  <div className="col-md-6">
                    <h6>Areas for Improvement</h6>
                    {selectedSupplier.keyIssues.map((issue, idx) => (
                      <div key={idx} className="small text-warning">• {issue}</div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setSelectedSupplier(null)}
                >
                  Close
                </button>
                <button type="button" className="btn btn-primary">
                  Export Supplier Report
                </button>
                <button type="button" className="btn btn-success">
                  Schedule Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EthicsSupplierEcosystem;